const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/reducers/ordersReducer.ts';
import Imm from 'immutable';
import moment from 'moment';
import _ from 'lodash';
import { Logger } from '../services/KisLoggerService';
import { getOrderId } from '../utils/orderHotspotEventUtils';
import { decCounter, incCounter } from '../utils/reducerUtils';
import {
    FORGET_ORDERS,
    IForgetOrdersAction,
    ILoadOrderRightsAction,
    ILoadOrdersAction,
    LOAD_ORDER_RIGHTS,
    LOAD_ORDERS,
} from '../actions/orderActions';

const Log = Logger('common_web/ordersReducer');

/**
 * Helper function to merge two list of hotspots. If two hotspots with the same
 * DAT are encountered, the one from newList will be taken.
 */
export function mergeHotspots(oldList: Imm.List<any>, newList: Imm.List<any>) {
    const map: any = {};

    oldList.forEach((hotspot: any) => {
        const dat: string = hotspot.get('DAT');

        if (!dat) {
            Log.warn('Skipping malformed hotspot in old list', hotspot);
        } else {
            map[dat] = hotspot;
        }
    });

    newList.forEach((hotspot: any) => {
        const dat: string = hotspot.get('DAT');

        if (!dat) {
            Log.warn('Skipping malformed hotspot in new list', hotspot);
        } else {
            map[dat] = hotspot;
        }
    });

    let result = Imm.List<any>();

    for (const dat in map) {
        if (map.hasOwnProperty(dat)) {
            result = result.push(map[dat]);
        }
    }

    return result.sort((h1: any, h2: any) => {
        const d1 = h1.get('DAT');
        const d2 = h2.get('DAT');
        return d1 < d2 ? -1 : d1 > d2 ? 1 : 0;
    }) as Imm.List<any>;
}

/**
 * Helper function to extract an order record from the single-record action payload.
 */
export function extractOrderRecord(payload: any, etag: string) {
    let rec = Imm.fromJS(payload || {});

    if (!etag) {
        Log.warn('extractOrderRecord: Missing ETAG!');
        // continue without etag
        rec = rec.delete('ETAG');
    } else {
        // Merge ETAG into record.
        rec = rec.set('ETAG', etag);
    }

    rec = rec.delete('Info'); // zap KISAPI extra information

    if (!rec.get('SENDER') || !rec.get('EVENTNR')) {
        Log.warn('extractOrderRecord: Extracted record is incomplete!');
        // continue
    }

    return rec;
}

/**
 * Helper function to put an order's record into the state. The index is
 * properly updated. Hotspot data is merged with the current state's record
 * unless the schedule has changed.
 */
export function putOrderIntoState(state: Imm.Map<string, any>, rec: Imm.Map<string, any>, patnr: string) {
    // Safety checks.

    if (rec.get('PATNR') !== patnr) {
        Log.error('Record with unexpected patnr (' + patnr + ') :', rec.toJS());
        return state;
    }

    // Retrieve the previous version of the record.

    const orderId = getOrderId(rec, 'common/ordersReducer');
    if (!orderId) {
        return state;
    }
    const oldRec = state.getIn(['records', orderId]);

    // Merge hotspot data.

    if (oldRec) {
        // Don't merge an order if there is already a newer version of that order in the store (might happen when
        // pausing an order and all orders are reloaded at the same time)
        const oldRecsMutationDate = moment(oldRec.get('MUTDAT')) || oldRec.get('DAT');
        const recsMutationDate = moment(rec.get('MUTDAT'));
        if (recsMutationDate.isBefore(oldRecsMutationDate)) {
            return state;
        }

        let forceNewHotspots = false;

        if (!oldRec.has('Applications')) {
            // No need to merge hotspot data as the old list is missing.
            forceNewHotspots = true;
        } else if (oldRec.get('STARTDAT') !== rec.get('STARTDAT')) {
            // A changed STARTDAT may render parts of the old list invalid!
            forceNewHotspots = true;
        } else if (oldRec.get('STOPDAT') !== rec.get('STOPDAT')) {
            // A changed STOPDAT may render parts of the old list invalid!
            forceNewHotspots = true;
        } else {
            const s1 = oldRec.getIn(['CONTENT', 'Schedule']) || Imm.Map<string, any>();
            const s2 = rec.getIn(['CONTENT', 'Schedule']) || Imm.Map<string, any>();

            if (!Imm.is(s1, s2)) {
                // A changed schedule may render parts of the old list invalid!
                forceNewHotspots = true;
            }
        }

        if (!forceNewHotspots) {
            // Merge the old list with the new one. We do this to allow the
            // App to load orders and their hotspots incrementally
            // as the time window grows.
            const merged = mergeHotspots(oldRec.get('Applications'), rec.get('Applications') || Imm.List<any>());
            rec = rec.set('Applications', merged);
        }
    }

    // Put the order into the flat list of records.

    state = state.setIn(['records', orderId], rec);

    // Update the index byPat.

    let index = state.getIn(['index', 'byPat', patnr]) || Imm.List();

    if (
        !index.find((id: string) => {
            return id === orderId;
        })
    ) {
        index = index.push(orderId);
        state = state.setIn(['index', 'byPat', patnr], index);
    }

    return state;
}

function _loadOrdersPending(state: Imm.Map<string, any>, action: ILoadOrdersAction) {
    if (action.meta.mindat && action.meta.maxdat && action.meta.withHotspots) {
        state = state.setIn(['currentTimeframe', action.meta.kat, 'min'], action.meta.mindat);
        state = state.setIn(['currentTimeframe', action.meta.kat, 'max'], action.meta.maxdat);
    } else {
        state = state.deleteIn(['currentTimeframe', action.meta.kat]);
    }
    return incCounter(state, ['loading', action.meta.patnr]);
}

function _loadOrdersSuccess(state: Imm.Map<string, any>, action: ILoadOrdersAction) {
    const payload: any = action && action.payload ? action.payload : {};
    let list: any[] = payload.List || [];

    if (!Array.isArray(list)) {
        // List may occasionally be an object.
        list = _.values(list);
    }

    //_veryBadButNecessaryDebugHack(list, action);

    list.forEach(item => {
        let rec = Imm.fromJS(item);

        if (!action.meta.withHotspots) {
            // Discard unexpected hotspots from the payload to ensure the ones
            // from the state are not overwritten.
            rec = rec.delete('Applications');
        } else if (action.meta.addApplicationManually) {
            rec = rec.set(
                'Applications',
                Imm.fromJS([
                    {
                        VAL: 'X',
                        UNIT: '',
                        KIND: '',
                        DAT: rec.get('STARTDAT'),
                    },
                ])
            );
        }

        if (action.meta.rType) {
            rec = rec.set('rType', action.meta.rType);
        }

        if (action.meta.evenIfDeleted || rec.get('STATUS') !== 'I') {
            state = putOrderIntoState(state, rec, action.meta.patnr);
        }
    });

    return decCounter(state, ['loading', action.meta.patnr]);
}

function _loadOrdersFailure(state: Imm.Map<string, any>, action: ILoadOrdersAction) {
    return decCounter(state, ['loading', action.meta.patnr]);
}

function _forgetOrders(state: Imm.Map<string, any>, action: IForgetOrdersAction) {
    const index = state.getIn(['index', 'byPat', action.patnr]);

    if (index) {
        index.forEach((eventId: string) => {
            state = state.deleteIn(['records', eventId]);
        });
    }

    return state.deleteIn(['index', 'byPat', action.patnr]);
}

function _loadOrderRightsPending(state: Imm.Map<string, any>, action: ILoadOrderRightsAction) {
    const orderId = `${action.meta.sender}_${action.meta.ordernr}`;
    return state.setIn(
        ['rights', orderId],
        Imm.fromJS({
            loading: 'pending',
        })
    );
}

function _loadOrderRightsSuccess(state: Imm.Map<string, any>, action: ILoadOrderRightsAction) {
    const orderId = `${action.meta.sender}_${action.meta.ordernr}`;
    state = state.setIn(['rights', orderId], Imm.fromJS(action.payload));
    return state.deleteIn(['rights', orderId, 'loading']);
}

function _loadOrderRightsFailure(state: Imm.Map<string, any>, action: ILoadOrderRightsAction) {
    const orderId = `${action.meta.sender}_${action.meta.ordernr}`;
    return state.setIn(['rights', orderId, 'loading'], 'failure');
}

/**
 * The initial values of the reducer's branch of the global state tree
 */
export const initialState = Imm.Map<string, any>();

/**
 * The actual reducer
 */
export function ordersReducer(state: any = initialState, action: any = {}) {
    switch (action.type) {
        case LOAD_ORDERS.pending:
            return _loadOrdersPending(state, action);
        case LOAD_ORDERS.success:
            return _loadOrdersSuccess(state, action);
        case LOAD_ORDERS.failure:
            return _loadOrdersFailure(state, action);

        case LOAD_ORDER_RIGHTS.pending:
            return _loadOrderRightsPending(state, action);
        case LOAD_ORDER_RIGHTS.success:
            return _loadOrderRightsSuccess(state, action);
        case LOAD_ORDER_RIGHTS.failure:
            return _loadOrderRightsFailure(state, action);

        case FORGET_ORDERS:
            return _forgetOrders(state, action);
    }
    return state;
}
